import React, { useContext, useEffect, useState } from 'react'
import { Context } from '../../Context/Context';
import NotificationListItem from './NotificationListItem';
import { ADMIN, clearApi, markAllAsReadApi, notificationsApi } from 'Constants';
import { toast } from 'react-toastify';
import { Pagination } from 'Components/Admin/Analytics/AdminAnalyticsUI';

// Design 14b - one reverse-chronological list with an "Unread only" filter
// (replaces the old Unread / Read tabs). Used by the attorney, admin and
// case-search notification views.
const PAGE_SIZE = 20;

export function UnreadToggle({ value, onChange }) {
    return (
        <label className='flex items-center cursor-pointer select-none' style={{ gap: '10px', fontSize: '14px', fontWeight: 500, color: 'rgba(20,33,61,.65)' }}>
            <span
                role='switch'
                aria-checked={value}
                onClick={() => onChange(!value)}
                className='relative inline-block transition-colors'
                style={{ width: '38px', height: '21px', borderRadius: '11px', background: value ? '#8a6231' : 'rgba(20,33,61,.18)' }}
            >
                <span className='absolute rounded-full transition-all' style={{ top: '3px', left: value ? '20px' : '3px', width: '15px', height: '15px', background: '#fffdf8' }} />
            </span>
            <span onClick={() => onChange(!value)}>Unread only</span>
        </label>
    );
}

function NotificationContent({attorney, isNotificationSearch = false, adminUserId, notificationArr = null, caseNumber = null, title = null, headerExtra = null})
{
    const {userDetails, makeApiCallFunc, setNotificationCount} = useContext(Context);
    const [items, setItems] = useState(notificationArr || []);
    const [loading, setLoading] = useState(!notificationArr);
    const [skip, setSkip] = useState(0);
    const [total, setTotal] = useState(0);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [updateList, setUpdateList] = useState(false);
    const [localArr, setLocalArr] = useState(notificationArr);

    const userInfo = (
        userDetails.role === ADMIN && userDetails?.isAdminAssistant ?
        adminUserId : (attorney ? attorney.userId : userDetails));
    // the logged-in user's own feed (drives the header bell count)
    const ownFeed = !attorney && !(userDetails.role === ADMIN && userDetails?.isAdminAssistant);

    useEffect(() => { setSkip(0); }, [unreadOnly]);

    useEffect(() => {
        if (localArr) {
            const list = unreadOnly ? localArr.filter((n) => n.is_read === false) : localArr;
            setItems(list);
            setTotal(list.length);
            setLoading(false);
            return;
        }
        if (!userInfo?._id) return;
        setLoading(true);
        makeApiCallFunc(notificationsApi+"/"+ userInfo?._id,
            {
                onlyCases: attorney ? true : false,
                skip,
                limit: PAGE_SIZE,
                ...(unreadOnly ? { isRead: false } : {}),
            },
            (data) => {
                setItems(data?.data?.notifications || []);
                setTotal(data?.data?.count || 0);
                setLoading(false);
            },
            (data) => { console.log(data); setLoading(false); },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminUserId, skip, unreadOnly, updateList, localArr]);

    const hasUnread = items.some((n) => !n.is_read);
    const hasRead = items.some((n) => n.is_read);

    const markAllAsRead = () => {
        makeApiCallFunc(
            markAllAsReadApi+"/"+userInfo?._id,
            {caseNumber},
            () => {
                toast.success('All notifications marked as read');
                if (ownFeed && !caseNumber) setNotificationCount(0);
                if (localArr) setLocalArr((prev) => prev.map((n) => ({ ...n, is_read: true })));
                else setUpdateList((prev) => !prev);
            },
            (data) => console.log(data),
        )
    }
    const clearRead = () => {
        makeApiCallFunc(
            clearApi+"/"+userInfo?._id,
            {caseNumber},
            ({data}) => {
                toast.success(data?.message || 'Read notifications cleared');
                if (localArr) setLocalArr((prev) => prev.filter((n) => !n.is_read));
                else { setSkip(0); setUpdateList((prev) => !prev); }
            },
            (data) => console.log(data),
        )
    }

    const pageItems = localArr ? items.slice(skip, skip + PAGE_SIZE) : items;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const page = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className='w-full font-plexsans'>
            <div className='flex items-center justify-between flex-wrap' style={{ gap: '14px', marginBottom: '14px' }}>
                {title ? <h2 style={{ margin: 0, font: "400 28px/1.2 'Spectral',serif", color: '#14213d' }}>{title}</h2> : <span />}
                <div className='flex items-center flex-wrap' style={{ gap: '22px' }}>
                    {headerExtra}
                    <UnreadToggle value={unreadOnly} onChange={setUnreadOnly} />
                    {hasUnread &&
                        <span onClick={markAllAsRead} className='cursor-pointer hover:text-tanDark' style={{ fontSize: '14px', fontWeight: 600, color: '#8a6231' }}>Mark all as read</span>
                    }
                    {!unreadOnly && hasRead && !isNotificationSearch &&
                        <span onClick={clearRead} className='cursor-pointer hover:text-navy' style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(20,33,61,.45)' }}>Clear read</span>
                    }
                </div>
            </div>

            {loading ?
                <div className='text-center' style={{ padding: '64px 0', fontSize: '13.5px', color: 'rgba(20,33,61,.4)' }}>Loading…</div>
            : pageItems.length === 0 ?
                <div className='text-center' style={{ padding: '64px 0', fontSize: '13.5px', color: 'rgba(20,33,61,.45)' }}>
                    {unreadOnly ? "You're all caught up" : "No notifications yet"}
                </div>
            :
                <div className='flex flex-col'>
                    {pageItems.map((msg, i) => (
                        <NotificationListItem
                            key={msg._id || i}
                            isNotificationSearch={isNotificationSearch}
                            attorney={attorney}
                            msg={msg}
                            caseNumber={caseNumber}
                            ownFeed={ownFeed}
                            isLast={i === pageItems.length - 1}
                            setUpdateList={localArr ? undefined : setUpdateList}
                        />
                    ))}
                </div>
            }

            {!loading && totalPages > 1 &&
                <div style={{ marginTop: '12px', border: '1px solid rgba(20,33,61,.1)', borderRadius: '6px', overflow: 'hidden' }}>
                    <Pagination page={page} totalPages={totalPages} onPage={(p) => { if (p >= 1 && p <= totalPages) { setSkip((p - 1) * PAGE_SIZE); window.scrollTo(0, 0); } }} />
                </div>
            }

            {!loading && pageItems.length > 0 && !isNotificationSearch &&
                <p className='text-center' style={{ margin: '28px 0 0', fontSize: '12.5px', color: 'rgba(20,33,61,.4)' }}>
                    Notifications older than 30 days are cleared automatically
                </p>
            }
        </div>
  )
}

export default NotificationContent
